import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { MedicineDictionarySortType, SearchMedicineDictionaryQuery } from '@/types/bff/gql-schema';
import { SearchResult } from '@/types/search';
import { QueryClient } from '@tanstack/react-query';
import { useSearchMedicineDictionaryQuery } from './search.gql.impl';

const KEYWORD_SEARCH_SIZE = 3;

const parseKeywordIntegratedSearchMedicineDictionaryVo = (
  data: SearchMedicineDictionaryQuery
): SearchResult<MedicineDictionaryModel> => {
  return {
    hasMore: data.searchMedicineDictionary.items.length > 1,
    elements: data.searchMedicineDictionary.items.map((medicine) => {
      return new MedicineDictionaryModel({
        id: medicine.id,
        publicId: medicine.publicId,
        basicInfo: {
          name: medicine.basicInfo.name,
          pharmaceuticalEffectLiteracy: medicine.basicInfo.pharmaceuticalEffectLiteracy,
          medicineImage: medicine.basicInfo.medicineImage ?? undefined,
          simplifiedName: medicine.basicInfo.simplifiedName,
        },
        medicineGuide: {
          wholeSummary: medicine.medicineGuide?.wholeSummary.join(' '),
        },
        packagingInfo: {
          image: medicine.packagingInfo.image ?? undefined,
        },
      });
    }),
    totalPage: data.searchMedicineDictionary.totalPage,
    totalCount: data.searchMedicineDictionary.totalCount,
  };
};

export enum EKeywordSearchMedicineDictionarySortType {
  Name = 'NAME',
  Score = 'SCORE',
}

interface Props {
  keyword?: string;
  sortType?: EKeywordSearchMedicineDictionarySortType;
  enabled?: boolean;
  limit?: number;
  page?: number;
}

const useGetKeywordIntegratedSearchMedicineDictionary = (props: Props = {}) => {
  const {
    keyword,
    sortType = EKeywordSearchMedicineDictionarySortType.Name,
    enabled = true,
    limit = KEYWORD_SEARCH_SIZE,
    page = 1,
  } = props;

  const { data: keywordSearchMedicineDictionary, isLoading: isKeywordSearchMedicineDictionaryLoading } =
    useSearchMedicineDictionaryQuery(
      {
        request: {
          ...(keyword && { keyword }),
          sortType:
            sortType === EKeywordSearchMedicineDictionarySortType.Name
              ? MedicineDictionarySortType.Name
              : MedicineDictionarySortType.Score,
          pagingRequest: {
            page: page,
            limit: limit,
          },
        },
      },
      {
        select: parseKeywordIntegratedSearchMedicineDictionaryVo,
        enabled: enabled,
        staleTime: 1000 * 60 * 5, // 5분 - SSR과 동일한 설정
      }
    );

  return {
    keywordSearchMedicineDictionary,
    totalPage: Math.floor(keywordSearchMedicineDictionary?.totalPage ?? 0),
    totalCount: keywordSearchMedicineDictionary?.totalCount ?? 0,
    isKeywordSearchMedicineDictionaryLoading,
  };
};

useGetKeywordIntegratedSearchMedicineDictionary.prefetchQuery = async (
  queryClient: QueryClient,
  options: {
    keyword?: string;
    limit?: number;
    page?: number;
  } = {}
) => {
  const { keyword, limit = KEYWORD_SEARCH_SIZE, page = 1 } = options;

  return queryClient.prefetchQuery({
    queryKey: useSearchMedicineDictionaryQuery.getKey({
      request: {
        ...(keyword && { keyword }),
        sortType: MedicineDictionarySortType.Name,
        pagingRequest: {
          page: page,
          limit: limit,
        },
      },
    }),
    queryFn: useSearchMedicineDictionaryQuery.fetcher({
      request: {
        ...(keyword && { keyword }),
        sortType: MedicineDictionarySortType.Name,
        pagingRequest: {
          page: page,
          limit: limit,
        },
      },
    }),
    staleTime: 1000 * 60 * 5, // 5분
  });
};

export default useGetKeywordIntegratedSearchMedicineDictionary;
